import { useState } from 'react';
import type { TemplatePackRead } from '../../types/api';
import { packIntent, type PackIntent } from './packIntents';
import { PackSwatch } from './PackSwatch';

const INTENT_TABS: readonly { value: PackIntent | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'people', label: 'People' },
  { value: 'products', label: 'Products' },
  { value: 'menu', label: 'Menus' },
  { value: 'offers', label: 'Offers' },
];

/** "1 image" / "2-5 slides" — the shapes a pack can take, as data. */
function shapeLine(pack: TemplatePackRead): string {
  const shapes: string[] = [];
  if (pack.post_types.includes('single_image')) shapes.push('1 image');
  if (pack.carousel) {
    shapes.push(`${pack.carousel.min_items}-${pack.carousel.max_items} slides`);
  }
  return shapes.join(' · ');
}

/** The preview picture when one is published, the blueprint sketch when not. */
function PackPreview({ pack }: { pack: TemplatePackRead }) {
  const [failed, setFailed] = useState(false);

  if (pack.preview_url === null || failed) {
    return <PackSwatch pack={pack} />;
  }
  return (
    <img
      className="pack-card__preview"
      src={pack.preview_url}
      alt={`${pack.name} sample`}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export interface PackGalleryProps {
  packs: readonly TemplatePackRead[];
  /** Starts the brief with this pack — the gallery's one action per card. */
  onUse: (pack: TemplatePackRead) => void;
}

/**
 * The template gallery: packs grouped by what the post is for.
 *
 * Every card carries the same three facts — the shape it draws, the fields it
 * asks for, and that a pack spends nothing on models — because those are the
 * reasons to pick one over a written brief. The card's whole action is "use
 * it": there is nothing to configure here, the brief screen owns the fields.
 */
export function PackGallery({ packs, onUse }: PackGalleryProps) {
  const [intent, setIntent] = useState<PackIntent | 'all'>('all');

  const visible = intent === 'all' ? packs : packs.filter((pack) => packIntent(pack) === intent);

  return (
    <div className="pack-gallery">
      <div className="pack-gallery__tabs" role="group" aria-label="Template purpose">
        {INTENT_TABS.map((tab) => (
          <button
            className={
              tab.value === intent ? 'platform-chip platform-chip--selected' : 'platform-chip'
            }
            type="button"
            key={tab.value}
            aria-pressed={tab.value === intent}
            onClick={() => setIntent(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="field__hint">No templates in this group yet.</p>
      ) : (
        <ul className="pack-grid">
          {visible.map((pack) => (
            <li className="pack-card" key={pack.slug}>
              <PackPreview pack={pack} />
              <span className="pack-card__name">{pack.name}</span>
              <span className="pack-card__description">{pack.description}</span>
              <span className="pack-card__meta">
                {shapeLine(pack)}
                <span className="pack-card__spend">Uses one post</span>
              </span>
              <div className="pack-card__actions">
                <button className="button" type="button" onClick={() => onUse(pack)}>
                  Use this template
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
